/**
 * HybridTrail — renders learning data for fast/hybrid conversations.
 *
 * These conversations have no runbook walk (no steps), so the guided-mode
 * trail UI does not apply. Instead the backend captures each question/answer
 * turn with the sources that were shown and cited.
 *
 * The engineer can mark each turn as helpful / partly / wrong / unknown, and
 * leave a one-line reason when marking "wrong". The marks are managed by the
 * parent (LearningMode) so they can ride the same persistence path.
 *
 * Degrades cleanly: if `hybrid` is absent or empty, renders nothing (the
 * parent is responsible for the empty state).
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Check, HelpCircle, AlertTriangle, X, ChevronRight } from "lucide-react";
import "./styles/hybrid-trail.css";

/* ── mark options for hybrid turns ─────────────────────────────────── */
const HYBRID_MARKS = [
  { value: "helpful", label: "Helpful", Icon: Check, cls: "ht-mark--helpful" },
  { value: "partly", label: "Partly", Icon: HelpCircle, cls: "ht-mark--partly" },
  { value: "wrong", label: "Wrong", Icon: X, cls: "ht-mark--wrong" },
];

/* ── cited badge ───────────────────────────────────────────────────── */
function CitedBadge({ cited }) {
  if (cited === true) return <span className="ht-badge ht-badge--cited">cited</span>;
  if (cited === false) return <span className="ht-badge ht-badge--not-cited">not cited</span>;
  // null / undefined = unknown: do not render a negative, just omit
  return null;
}

/* ── source card ───────────────────────────────────────────────────── */
function SourceCard({ source }) {
  if (!source || typeof source !== "object") return null;
  const title = source.title || "Untitled source";
  const score = typeof source.score === "number" ? source.score.toFixed(2) : null;
  const sourceType = source.source_type || null;

  return (
    <div className="ht-source">
      <span className="ht-source-title">{title}</span>
      {score && <span className="ht-source-score">{score}</span>}
      {sourceType && <span className="ht-source-type">{sourceType}</span>}
      <CitedBadge cited={source.cited} />
    </div>
  );
}

/* ── single turn block ─────────────────────────────────────────────── */

/**
 * A real answer runs several hundred words of markdown and drags two source
 * lists behind it. Printed in full it buries the question it answers and, in
 * the merged turn timeline, the rest of the conversation with it. Past this
 * many characters the body folds behind a disclosure and the card shows the
 * question plus a one-line preview. Short answers are untouched — a
 * disclosure over two lines of text is pure friction.
 */
const LONG_ANSWER_CHARS = 320;

/** First line / first sentence of a long answer, for the collapsed preview. */
function answerPreview(text) {
  const flat = String(text).replace(/\s+/g, " ").trim();
  return flat.length > 140 ? `${flat.slice(0, 139).trimEnd()}…` : flat;
}

/** Same text, ignoring whitespace runs and the ends. */
const sameText = (a, b) =>
  String(a ?? "").replace(/\s+/g, " ").trim() === String(b ?? "").replace(/\s+/g, " ").trim();

/**
 * `showHeader` — the "Turn N" strip. The standalone list wants it; inside a
 * merged turn card the surrounding card already says which turn this is, so
 * repeating it reads as a bug.
 * `showQuestion` — the ASKED block. The turn breakdown drops it when its row
 * title already reads `Asked: "<the same question>"`.
 */
function HybridTurn({
  turn,
  mark,
  reason,
  readOnly,
  onMark,
  onReason,
  showHeader = true,
  showQuestion = true,
  confirmed = false,
}) {
  const [showReason, setShowReason] = useState(!!reason);
  const turnNo = turn.turn_no;
  const longAnswer =
    typeof turn.answer_text === "string" && turn.answer_text.length > LONG_ANSWER_CHARS;
  const [showBody, setShowBody] = useState(!longAnswer);

  const handleMark = useCallback(
    (value) => {
      if (readOnly) return;
      onMark(turnNo, value);
      if (value === "wrong") setShowReason(true);
    },
    [turnNo, readOnly, onMark]
  );

  const handleReason = useCallback(
    (e) => {
      onReason(turnNo, e.target.value);
    },
    [turnNo, onReason]
  );

  /* The reason field wraps and grows (2 lines at rest, 4 at most — see
     .ht-reason-input) instead of clipping an AI-written sentence. */
  const reasonRef = useRef(null);
  useLayoutEffect(() => {
    const el = reasonRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
  }, [reason, showReason, mark]);

  const reasonShown = (showReason || mark === "wrong") && !readOnly;
  /* The backend's `why` seeds the editable reason (LearningMode's
     hybridReasons). While the field still holds that same text, printing it
     again as a ⚠ line underneath is the same sentence twice: the field shows
     it once, tagged "AI suggested", and the ⚠ line is kept for when the two
     differ (the engineer rewrote it) or there is no field (read-only). */
  const reasonIsAiWhy = reasonShown && !!turn.why && sameText(reason, turn.why);

  const sourcesShown = Array.isArray(turn.sources_shown) ? turn.sources_shown : [];
  const sourcesCited = Array.isArray(turn.sources_cited) ? turn.sources_cited : [];
  const hasAnySources = sourcesShown.length > 0 || sourcesCited.length > 0;

  return (
    <div
      className={`ht-turn${mark ? ` ht-turn--${mark}` : ""}`}
      data-turn={turnNo}
      data-unmarked={!mark ? "true" : undefined}
    >
      {showHeader && (
        <div className="ht-turn-header">
          <span className="ht-turn-label">Turn {turnNo}</span>
          {turn.result && turn.result !== "unknown" && (
            <span className="ht-turn-result">{turn.result}</span>
          )}
        </div>
      )}

      {/* Question */}
      {showQuestion && turn.question_text && (
        <div className="ht-question">
          <p className="ht-q-label">Asked</p>
          <p className="ht-q-text">{turn.question_text}</p>
        </div>
      )}

      {/* Answer — folded behind a disclosure when it is a long one. The
          preview stays on screen either way, so the card never reads as
          empty while collapsed. */}
      {turn.answer_text && (
        <div className="ht-answer">
          <p className="ht-a-label">Answered</p>
          {longAnswer && !showBody && (
            <p className="ht-a-preview">{answerPreview(turn.answer_text)}</p>
          )}
          {(!longAnswer || showBody) && <p className="ht-a-text">{turn.answer_text}</p>}
        </div>
      )}

      {longAnswer && (
        <button
          type="button"
          className="ht-disclosure"
          aria-expanded={showBody}
          onClick={(e) => {
            e.stopPropagation();
            setShowBody((v) => !v);
          }}
        >
          <ChevronRight size={12} className={showBody ? "ht-disclosure-open" : ""} />
          <span>{showBody ? "Hide the full answer" : "Show the full answer"}</span>
        </button>
      )}

      {/* Sources — they belong to the answer, so they fold with it. */}
      {hasAnySources && (!longAnswer || showBody) && (
        <div className="ht-sources">
          {sourcesShown.length > 0 && (
            <div className="ht-sources-group">
              <p className="ht-sources-label">
                Sources shown ({sourcesShown.length})
              </p>
              <div className="ht-sources-list">
                {sourcesShown.map((s, i) => (
                  <SourceCard key={i} source={s} />
                ))}
              </div>
            </div>
          )}
          {sourcesCited.length > 0 && (
            <div className="ht-sources-group">
              <p className="ht-sources-label">
                Sources cited ({sourcesCited.length})
              </p>
              <div className="ht-sources-list">
                {sourcesCited.map((s, i) => (
                  <SourceCard key={i} source={s} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mark controls */}
      {!readOnly && (
        <div className="ht-marks">
          {HYBRID_MARKS.map((m) => (
            <button
              key={m.value}
              type="button"
              className={`ht-mark ${m.cls}${mark === m.value ? " ht-mark--active" : ""}`}
              onClick={() => handleMark(m.value)}
              title={m.label}
            >
              <m.Icon size={13} />
              <span>{m.label}</span>
            </button>
          ))}
          {mark && turn.result_source === "inferred" && !confirmed && (
            <span className="tr-ai-badge" title="Suggested by AI — click a mark to confirm">AI</span>
          )}
        </div>
      )}

      {/* Reason input for "wrong" */}
      {reasonShown && (
        <div className="ht-reason">
          {reasonIsAiWhy && (
            <span className="ht-reason-ai" title="Written by AI — edit it or leave it">
              AI suggested
            </span>
          )}
          <textarea
            ref={reasonRef}
            rows={2}
            className="ht-reason-input"
            placeholder="What was wrong?"
            aria-label="Reason for this mark"
            value={reason || ""}
            onChange={handleReason}
          />
        </div>
      )}

      {/* Why note from backend — unless the field above already shows it. */}
      {turn.why && !reasonIsAiWhy && (
        <div className="ht-why">
          <AlertTriangle size={12} />
          <span>{turn.why}</span>
        </div>
      )}
    </div>
  );
}

/* ── main component ────────────────────────────────────────────────── */

/**
 * @param {Object} props
 * @param {Array}  props.hybrid     - Array of hybrid turn objects from the backend
 * @param {Object} props.marks      - { [turn_no]: "helpful"|"partly"|"wrong" }
 * @param {Object} props.reasons    - { [turn_no]: string }
 * @param {boolean} props.readOnly
 * @param {Function} props.onMark   - (turnNo, value) => void
 * @param {Function} props.onReason - (turnNo, text) => void
 */
export default function HybridTrail({
  hybrid = [],
  marks = {},
  reasons = {},
  readOnly = false,
  onMark,
  onReason,
}) {
  if (!Array.isArray(hybrid) || hybrid.length === 0) return null;

  const unmarkedCount = hybrid.filter((t) => !marks[t.turn_no]).length;

  return (
    <div className="ht-root">
      <div className="ht-header">
        <h2 className="tr-heading">
          Conversation turns
          <span className="tr-heading-count">
            {hybrid.length} turn{hybrid.length > 1 ? "s" : ""}
          </span>
        </h2>
        {!readOnly && unmarkedCount > 0 && (
          <span className="ht-unmarked-hint">
            {unmarkedCount} to mark
          </span>
        )}
      </div>
      <div className="ht-turns">
        {hybrid.map((turn) => (
          <HybridTurn
            key={turn.turn_no}
            turn={turn}
            mark={marks[turn.turn_no]}
            reason={reasons[turn.turn_no]}
            readOnly={readOnly}
            onMark={onMark}
            onReason={onReason}
          />
        ))}
      </div>
    </div>
  );
}

/* Exported for testing */
export { HYBRID_MARKS, CitedBadge, SourceCard, HybridTurn, LONG_ANSWER_CHARS };
